import { ModelData, ModelPair, DeviationData, BaselineComparisonData } from './types';
import { ChartDataPoint } from '../chart-card/types';
import { ModelState } from '../../contexts/types';

export const getColorIntensity = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue === 0) return 'bg-gray-400 dark:bg-gray-600'; // diagonal (same model)
  if (absValue <= 2) return 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'; // high consensus
  if (absValue <= 4) return 'bg-pink-100 dark:bg-pink-500/20'; // medium
  if (absValue <= 6) return 'bg-pink-300 dark:bg-pink-500/60'; // disagreement
  return 'bg-pink-500 dark:bg-pink-500'; // high disagreement (Jua pink)
};

export const generateModelPairs = (models: string[], modelData: ModelData[]): ModelPair[] => {
  const modelPairs: ModelPair[] = [];

  models.forEach((model1, i) => {
    models.forEach((model2, j) => {
      if (i < j) {
        // Only add each pair once (upper triangle)
        const model1Data = modelData.find((m) => m.model === model1);
        const disagreementValue = model1Data?.values[model2] || 0;
        const absValue = Math.abs(disagreementValue);

        let category = '';
        let colorClass = '';

        if (absValue <= 2) {
          category = 'High Consensus';
          colorClass = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700';
        } else if (absValue <= 4) {
          category = 'Medium Disagreement';
          colorClass = 'bg-pink-100 dark:bg-pink-500/20';
        } else if (absValue <= 6) {
          category = 'High Disagreement';
          colorClass = 'bg-pink-300 dark:bg-pink-500/60';
        } else {
          category = 'Very High Disagreement';
          colorClass = 'bg-pink-500 dark:bg-pink-500';
        }

        modelPairs.push({
          model1,
          model2,
          disagreement: disagreementValue,
          category,
          colorClass,
        });
      }
    });
  });

  // Sort by disagreement level (highest first)
  return modelPairs.sort((a, b) => Math.abs(b.disagreement) - Math.abs(a.disagreement));
};

const getAverageByDate = (data: { [date: string]: { [modelLabel: string]: number[] } }) => {
  const result: { [date: string]: number } = {};

  for (const [date, models] of Object.entries(data)) {
    // Get all values from all models for this date
    const allValues = [];

    for (const modelValues of Object.values(models)) {
      allValues.push(...modelValues);
    }

    // Calculate average
    const average =
      allValues.length > 0 ? allValues.reduce((sum, val) => sum + val, 0) / allValues.length : 0;

    result[date] = average;
  }

  return result;
};

/**
 * Calculate deviations from mean for selected models (supports both daily and hourly modes)
 */
export const calculateDeviationData = (
  chartData: ChartDataPoint[],
  selectedModels: ModelState[],
): {
  deviationData: DeviationData[];
  dates: string[];
  timesteps: string[];
  dailyMeans: { [date: string]: number };
  timestepMeans: { [timestep: string]: number };
  statisticalContext: { std: number; thresholds: [number, number, number] };
} => {
  if (chartData.length === 0 || selectedModels.length === 0) {
    return {
      deviationData: [],
      dates: [],
      timesteps: [],
      dailyMeans: {},
      timestepMeans: {},
      statisticalContext: { std: 1, thresholds: [0.5, 1, 2] },
    };
  }

  // Get selected model labels that have runs
  const selectedModelLabels = selectedModels
    .filter((model) => model.isSelected && model.selectedRuns.length > 0)
    .flatMap((model) =>
      model.selectedRuns.map((run) => `${model.label} | ${run.date} ${run.time}:00`),
    );

  if (selectedModelLabels.length === 0) {
    return { deviationData: [], dates: [], timesteps: [], dailyMeans: {}, timestepMeans: {} };
  }

  // Check if only one model run is selected - deviations should be zero since there's no other model run to compare against
  const isSingleModelRun = selectedModelLabels.length === 1;

  // Extract unique dates and timesteps from chart data
  const dates = chartData.map((point) => {
    const date = new Date(point.time);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const uniqueDates = [...new Set(dates)];
  const timesteps = chartData.map((point) => point.time);

  // Group data by date and calculate daily means
  const dailyData: { [date: string]: { [modelLabel: string]: number[] } } = {};

  // For hourly mode, also track timestep-level means
  const timestepMeans: { [timestep: string]: number } = {};

  chartData.forEach((point, index) => {
    const date = dates[index];
    const timestep = point.time;

    if (!dailyData[date]) {
      dailyData[date] = {};
    }

    const allValuesForTimestep: number[] = [];

    selectedModelLabels.forEach((modelLabel) => {
      const value = point[modelLabel] as number;
      if (value !== undefined && value !== null) {
        if (!dailyData[date][modelLabel]) {
          dailyData[date][modelLabel] = [];
        }
        dailyData[date][modelLabel].push(value);
        allValuesForTimestep.push(value);
      }
    });

    // Calculate mean for this timestep across all models (for hourly mode)
    if (allValuesForTimestep.length > 0) {
      timestepMeans[timestep] =
        allValuesForTimestep.reduce((sum, val) => sum + val, 0) / allValuesForTimestep.length;
    }
  });

  const dailyMeans = getAverageByDate(dailyData);

  // Calculate deviations for each model - only include models with sufficient data
  const deviationData: DeviationData[] = selectedModels
    .filter((model) => model.isSelected && model.selectedRuns.length > 0)
    .flatMap((model) =>
      model.selectedRuns.map((run) => {
        const modelLabel = `${model.label} | ${run.date} ${run.time}:00`;
        const dailyDeviations: { [date: string]: number } = {};
        const timestepDeviations: { [timestep: string]: number } = {};

        // Check if this model has data in the chart
        const hasDataInChart = chartData.some((point) => {
          const value = point[modelLabel] as number;
          return value !== undefined && value !== null;
        });

        // Skip this model if it has no data in the chart (prevents all-zero rows)
        if (!hasDataInChart) {
          return null;
        }

        let validDailyDeviations = 0;
        let validTimestepDeviations = 0;

        // Calculate daily deviations
        uniqueDates.forEach((date) => {
          const modelValues = dailyData[date]?.[modelLabel] || [];
          const meanValue = dailyMeans[date];

          if (modelValues.length > 0 && meanValue !== undefined) {
            // If only one model run is selected, deviation should always be zero since there's no other model run to compare against
            if (isSingleModelRun) {
              dailyDeviations[date] = 0;
            } else {
              const modelMean = modelValues.reduce((sum, val) => sum + val, 0) / modelValues.length;
              dailyDeviations[date] = modelMean - meanValue;
            }
            validDailyDeviations++;
          } else {
            // Use null to indicate missing data instead of 0
            dailyDeviations[date] = null as any;
          }
        });

        // Calculate timestep-level deviations for hourly mode
        chartData.forEach((point) => {
          const timestep = point.time;
          const modelValue = point[modelLabel] as number;
          const meanValue = timestepMeans[timestep];

          if (modelValue !== undefined && modelValue !== null && meanValue !== undefined) {
            // If only one model run is selected, deviation should always be zero since there's no other model run to compare against
            if (isSingleModelRun) {
              timestepDeviations[timestep] = 0;
            } else {
              timestepDeviations[timestep] = modelValue - meanValue;
            }
            validTimestepDeviations++;
          } else {
            // Use null to indicate missing data instead of 0
            timestepDeviations[timestep] = null as any;
          }
        });

        // Only include this model if it has at least some valid deviations
        // This prevents models that are still loading from appearing with all zeros
        const hasValidDailyData = validDailyDeviations > 0;
        const hasValidTimestepData = validTimestepDeviations > 0;

        if (!hasValidDailyData && !hasValidTimestepData) {
          return null;
        }

        return {
          modelId: model.id,
          modelLabel,
          dailyDeviations,
          timestepDeviations,
        };
      }),
    )
    .filter((deviation): deviation is DeviationData => deviation !== null); // Remove null entries

  // Calculate statistical context for color normalization
  const allDeviations: (number | null)[] = [];
  deviationData.forEach((model) => {
    Object.values(model.dailyDeviations).forEach((dev) => allDeviations.push(dev));
    Object.values(model.timestepDeviations).forEach((dev) => allDeviations.push(dev));
  });
  const statisticalContext = calculateStatisticalThresholds(allDeviations);

  return {
    deviationData,
    dates: uniqueDates.sort(),
    timesteps,
    dailyMeans,
    timestepMeans,
    statisticalContext,
  };
};

/**
 * Calculate statistical thresholds for color mapping based on standard deviation
 */
export const calculateStatisticalThresholds = (
  values: (number | null)[],
): {
  std: number;
  thresholds: [number, number, number]; // [0.5σ, 1σ, 2σ]
} => {
  // Filter out null values and calculate statistics
  const validValues = values.filter((v): v is number => v !== null);

  if (validValues.length === 0) {
    return { std: 1, thresholds: [0.5, 1, 2] }; // fallback to fixed thresholds
  }

  // Calculate standard deviation of the deviation values
  const mean = validValues.reduce((sum, val) => sum + Math.abs(val), 0) / validValues.length;
  const variance =
    validValues.reduce((sum, val) => sum + Math.pow(Math.abs(val) - mean, 2), 0) /
    validValues.length;
  const std = Math.sqrt(variance);

  // Use statistical thresholds: 0.5σ, 1σ, 2σ
  // This provides better differentiation across different data distributions
  return {
    std,
    thresholds: [std * 0.5, std * 1.0, std * 2.0],
  };
};

/**
 * Get color intensity for deviation values with statistical normalization
 */
export const getDeviationColorIntensity = (
  deviation: number | null,
  statisticalContext?: { std: number; thresholds: [number, number, number] },
): string => {
  // Handle missing data with distinct styling
  if (deviation === null) {
    return 'bg-gray-200 dark:bg-gray-600 border-2 border-dashed border-gray-400 dark:border-gray-500'; // missing data
  }

  const absDeviation = Math.abs(deviation);

  if (absDeviation === 0) {
    return 'bg-gray-100 dark:bg-gray-700'; // no deviation
  }

  // Use statistical thresholds if available, otherwise fall back to fixed thresholds
  const [t1, t2, t3] = statisticalContext?.thresholds || [1, 2, 3];

  if (deviation < 0) {
    // Negative deviations: blue shades (below mean)
    if (absDeviation <= t1) return 'bg-blue-100 dark:bg-blue-900/60';
    if (absDeviation <= t2) return 'bg-blue-200 dark:bg-blue-800/70';
    if (absDeviation <= t3) return 'bg-blue-300 dark:bg-blue-700/80';
    return 'bg-blue-400 dark:bg-blue-600/90';
  } else {
    // Positive deviations: pink shades (above mean)
    if (absDeviation <= t1) return 'bg-pink-100 dark:bg-pink-900/60';
    if (absDeviation <= t2) return 'bg-pink-200 dark:bg-pink-800/70';
    if (absDeviation <= t3) return 'bg-pink-300 dark:bg-pink-700/80';
    return 'bg-pink-400 dark:bg-pink-600/90';
  }
};

/**
 * Get text color for deviation values to ensure readability
 */
export const getDeviationTextColor = (deviation: number | null): string => {
  // Handle missing data with distinct text color
  if (deviation === null) {
    return 'text-gray-500 dark:text-gray-400';
  }

  const absDeviation = Math.abs(deviation);

  if (absDeviation === 0) {
    return 'text-gray-700 dark:text-gray-300';
  }

  if (deviation < 0) {
    // Blue backgrounds - use appropriate contrast
    if (absDeviation <= 2) return 'text-blue-800 dark:text-blue-100';
    return 'text-blue-900 dark:text-blue-50';
  } else {
    // Pink backgrounds - use appropriate contrast
    if (absDeviation <= 2) return 'text-pink-800 dark:text-pink-100';
    return 'text-pink-900 dark:text-pink-50';
  }
};

/**
 * Calculate baseline comparison data for selected models
 */
export const calculateBaselineComparison = (
  chartData: ChartDataPoint[],
  selectedModels: ModelState[],
  baselineLabel: string,
  aggregationMode: 'hourly' | 'daily' = 'hourly',
): {
  comparisonData: BaselineComparisonData[];
  timesteps: string[];
  availableBaselines: string[];
  statisticalContext: { std: number; thresholds: [number, number, number] };
} => {
  if (chartData.length === 0 || selectedModels.length === 0) {
    return {
      comparisonData: [],
      timesteps: [],
      availableBaselines: [],
      statisticalContext: { std: 1, thresholds: [0.5, 1, 2] },
    };
  }

  // Get selected model labels that have runs
  const selectedModelLabels = selectedModels
    .filter((model) => model.isSelected && model.selectedRuns.length > 0)
    .flatMap((model) =>
      model.selectedRuns.map((run) => `${model.label} | ${run.date} ${run.time}:00`),
    );

  if (selectedModelLabels.length === 0) {
    return {
      comparisonData: [],
      timesteps: [],
      availableBaselines: [],
      statisticalContext: { std: 1, thresholds: [0.5, 1, 2] },
    };
  }

  // Use the first available model as baseline if none specified or if specified one is not available
  const actualBaseline = selectedModelLabels.includes(baselineLabel)
    ? baselineLabel
    : selectedModelLabels[0];

  // Process data based on aggregation mode
  let processedData: {
    timesteps: string[];
    dataByTimestep: { [timestep: string]: { [modelLabel: string]: number } };
  };

  if (aggregationMode === 'daily') {
    // Group by day and calculate daily averages
    const dailyData: { [day: string]: { [modelLabel: string]: number[] } } = {};

    chartData.forEach((point) => {
      const day = new Date(point.time).toISOString().split('T')[0]; // YYYY-MM-DD format
      if (!dailyData[day]) {
        dailyData[day] = {};
      }

      selectedModelLabels.forEach((modelLabel) => {
        const value = point[modelLabel] as number;
        if (value !== undefined && value !== null) {
          if (!dailyData[day][modelLabel]) {
            dailyData[day][modelLabel] = [];
          }
          dailyData[day][modelLabel].push(value);
        }
      });
    });

    // Calculate daily averages
    const timesteps = Object.keys(dailyData).sort();
    const dataByTimestep: { [timestep: string]: { [modelLabel: string]: number } } = {};

    timesteps.forEach((day) => {
      dataByTimestep[day] = {};
      selectedModelLabels.forEach((modelLabel) => {
        const values = dailyData[day][modelLabel] || [];
        if (values.length > 0) {
          dataByTimestep[day][modelLabel] =
            values.reduce((sum, val) => sum + val, 0) / values.length;
        } else {
          // Mark missing as null so UI can render N/A and avoid implying zeros
          (dataByTimestep[day] as any)[modelLabel] = null;
        }
      });
    });

    processedData = { timesteps, dataByTimestep };
  } else {
    // Hourly timestep mode (original behavior)
    const timesteps = chartData.map((point) => point.time);
    const dataByTimestep: { [timestep: string]: { [modelLabel: string]: number | null } } = {};

    chartData.forEach((point) => {
      const timestep = point.time;
      dataByTimestep[timestep] = {} as any;
      selectedModelLabels.forEach((modelLabel) => {
        const value = point[modelLabel] as number;
        // Preserve null for missing values rather than coercing to zero
        (dataByTimestep[timestep] as any)[modelLabel] =
          value !== undefined && value !== null ? value : null;
      });
    });

    processedData = { timesteps, dataByTimestep };
  }

  // Calculate differences from baseline for each model at each timestep/day
  const comparisonData: BaselineComparisonData[] = selectedModelLabels
    .filter((modelLabel) => modelLabel !== actualBaseline) // Exclude baseline from comparison
    .map((modelLabel) => {
      const timestepDifferences: { [timestep: string]: number } = {};

      // Check if this model has data in the chart
      const hasDataInChart = chartData.some((point) => {
        const value = point[modelLabel] as number;
        return value !== undefined && value !== null;
      });

      // Skip this model if it has no data in the chart (prevents all-zero rows)
      if (!hasDataInChart) {
        return null;
      }

      let validDifferences = 0;

      processedData.timesteps.forEach((timestep) => {
        const baselineValue = processedData.dataByTimestep[timestep][actualBaseline];
        const modelValue = processedData.dataByTimestep[timestep][modelLabel];

        if (
          baselineValue !== undefined &&
          baselineValue !== null &&
          modelValue !== undefined &&
          modelValue !== null
        ) {
          timestepDifferences[timestep] = modelValue - baselineValue;
          validDifferences++;
        } else {
          // Missing data for this model at this timestep → mark as null so UI can display N/A
          (timestepDifferences as any)[timestep] = null;
        }
      });

      // Only include this model if it has at least some valid differences
      if (validDifferences === 0) {
        return null;
      }

      return {
        modelId: modelLabel.split(' | ')[0], // Extract model name before the timestamp
        modelLabel,
        timestepDifferences,
      };
    })
    .filter((comparison): comparison is BaselineComparisonData => comparison !== null); // Remove null entries

  // Calculate statistical context for color normalization
  const allDifferences: number[] = [];
  comparisonData.forEach((model) => {
    Object.values(model.timestepDifferences).forEach((diff) => {
      if (typeof diff === 'number') allDifferences.push(diff);
    });
  });
  const statisticalContext = calculateStatisticalThresholds(allDifferences);

  return {
    comparisonData,
    timesteps: processedData.timesteps,
    availableBaselines: selectedModelLabels,
    statisticalContext,
  };
};

/**
 * Get color intensity for baseline comparison differences with statistical normalization
 */
export const getBaselineComparisonColor = (
  difference: number,
  statisticalContext?: { std: number; thresholds: [number, number, number] },
): string => {
  const absDifference = Math.abs(difference);

  if (absDifference === 0) {
    return 'bg-gray-100 dark:bg-gray-700'; // no difference
  }

  // Use statistical thresholds if available, otherwise fall back to fixed thresholds
  const [t1, t2, t3] = statisticalContext?.thresholds || [1, 2, 3];

  if (difference < 0) {
    // Negative differences: blue shades (below baseline)
    if (absDifference <= t1) return 'bg-blue-100 dark:bg-blue-900/60';
    if (absDifference <= t2) return 'bg-blue-200 dark:bg-blue-800/70';
    if (absDifference <= t3) return 'bg-blue-300 dark:bg-blue-700/80';
    return 'bg-blue-400 dark:bg-blue-600/90';
  } else {
    // Positive differences: pink shades (above baseline)
    if (absDifference <= t1) return 'bg-pink-100 dark:bg-pink-900/60';
    if (absDifference <= t2) return 'bg-pink-200 dark:bg-pink-800/70';
    if (absDifference <= t3) return 'bg-pink-300 dark:bg-pink-700/80';
    return 'bg-pink-400 dark:bg-pink-600/90';
  }
};

/**
 * Get text color for baseline comparison differences
 */
export const getBaselineComparisonTextColor = (difference: number): string => {
  const absDifference = Math.abs(difference);

  if (absDifference === 0) {
    return 'text-gray-700 dark:text-gray-300';
  }

  if (difference < 0) {
    // Blue backgrounds - use appropriate contrast
    if (absDifference <= 2) return 'text-blue-800 dark:text-blue-100';
    return 'text-blue-900 dark:text-blue-50';
  } else {
    // Pink backgrounds - use appropriate contrast
    if (absDifference <= 2) return 'text-pink-800 dark:text-pink-100';
    return 'text-pink-900 dark:text-pink-50';
  }
};
